// Quote documents (#2018): what the web works out from a quote intake view. The person's
// choices live in a Review; gate() says whether Confirm can go, and confirmRequest() is
// exactly what it sends. Nothing here calls the server.
import {
  INTAKE_LIMITS,
  onebillCodeStem,
  parsePrintedMoney,
  sourceName,
  type CatalogCodeReport,
  type CatalogProductV2,
  type CatalogSyncState,
  type Cents,
  type ContractTerm,
  type CustomerContact,
  type CustomerSummary,
  type IntakeConfirmContactInput,
  type IntakeConfirmLineDecision,
  type IntakeConfirmRequest,
  type IntakeContact,
  type IntakeLine,
  type IntakePartKind,
  type PricedProduct,
  type Quote,
  type QuoteIntake,
  type QuoteIntakeLineMatch,
  type QuoteIntakeLineView,
  type QuoteIntakeSource,
  type QuoteIntakeSourceView,
  type QuoteIntakeState,
  type QuoteIntakeView,
  type Via,
} from "@phoneware/lens-domain";
import { plural } from "./format.js";

/** POST /api/quote-intakes/:id/confirm answers with what it created. */
export interface IntakeConfirmResult {
  customer: CustomerSummary;
  quote: Quote;
  contacts: CustomerContact[];
}

/** The upload's answer. `again` when the server already had this source from this dealer. */
export interface IntakeUpload {
  intake: QuoteIntakeView;
  again: boolean;
}

/** The server's limit (express.raw, limit "10mb"). The web refuses a bigger file before sending it. */
export const MAX_INTAKE_BYTES = INTAKE_LIMITS.fileBytes;
export const MAX_PDF_BYTES = MAX_INTAKE_BYTES;

export type IntakeView = "extracting" | "ready" | "nolines" | "failed" | "confirmed" | "discarded";

type Viewish = { state: QuoteIntakeState; extraction?: { result: { lines: readonly unknown[] } } | null; lineCount?: number | null };

export function intakeView(intake: Viewish): IntakeView {
  const lines = intake.extraction ? intake.extraction.result.lines.length : intake.lineCount;
  if (intake.state === "ready" && lines === 0) return "nolines";
  return intake.state;
}

export function isOpenIntake(intake: { state: QuoteIntakeState }): boolean {
  return intake.state !== "confirmed" && intake.state !== "discarded";
}

export function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function viaLine(via: Via, by: string, name: (email: string) => string, me: string): string {
  if (via.kind !== "web") return `Sent by ${via.clientName} for ${name(by)}`;
  return `Uploaded by ${by === me ? "you" : name(by)}`;
}

export function billWord(billing: "monthly" | "one_time" | "unknown" | null): string {
  return billing === "monthly" ? "monthly" : billing === "one_time" ? "one-time" : "unknown";
}

export function codeLine(p: PricedProduct): string {
  return (p.billing === "monthly" ? [p.code, "per month"] : [p.code, p.category, "each"]).filter(Boolean).join(" \u00b7 ");
}

export function sharedWords(printedName: string, name: string): string {
  const [ours, theirs] = [printedName, name].map((s) => s.toLowerCase().match(/[a-z0-9]+/g) ?? []);
  const mine = new Set(ours);
  const shared = theirs.filter((w) => mine.has(w)).length;
  const total = Math.max(ours.length, theirs.length, 1);
  return `${shared} of ${total} words in the name`;
}

export const joinAnd = (items: string[]): string => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

export function contactName(c: { firstName?: string | null; lastName?: string | null; email?: string | null }): string {
  return [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.email || "A contact with no name";
}

export function lineText(l: IntakeLine): string {
  return l.description.trim() || l.text.trim();
}

export function printedBilling(l: IntakeLine): "monthly" | "one_time" | "unknown" {
  if (l.billing !== "unknown") return l.billing;
  return parsePrintedMoney(l.unitPrice)?.billingHint ?? "unknown";
}

export function intakeNumberOf(q: Pick<Quote, "intakeId">): string | null {
  return q.intakeId ?? null;
}

export function sourceLabel(source: QuoteIntakeSource | QuoteIntakeSourceView): string {
  return sourceName(source);
}

export function sourceKind(source: QuoteIntakeSource | QuoteIntakeSourceView): string {
  if (source.kind === "file") return source.format;
  if (source.kind === "kqm") return "kqm";
  return source.textKind;
}

export function partChip(part: { kind: IntakePartKind; label: string } | undefined, page: number): string {
  if (!part || part.kind === "pdf") return `p${page}`;
  return part.label;
}

export type CustomerChoice =
  | { kind: "existing"; id: number; why: "phone" | "name" | "company_key" | "autotask_id" | "picked" | "candidate" }
  | { kind: "new" };

export interface ProspectForm {
  name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface LineChoice {
  pick: string | null;
  out: boolean;
}

export interface ContactEdit {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
}

export interface AddedContact extends ContactEdit {
  key: string;
  include: boolean;
}

export interface Review {
  customer: CustomerChoice;
  form: ProspectForm;
  checked: boolean[];
  edits: ContactEdit[];
  added: AddedContact[];
  primary: number;
  existingPrimaryId: number | null;
  term: ContractTerm | null;
  lines: LineChoice[];
  note: string;
}

type LineLike = QuoteIntakeLineView | QuoteIntakeLineMatch;

export type Reviewable = {
  extraction: QuoteIntake["extraction"];
  match:
    | {
        customer: NonNullable<QuoteIntake["match"]>["customer"];
        lines: LineLike[];
        totals: NonNullable<QuoteIntake["match"]>["totals"];
      }
    | null;
};

function isViewLine(line: LineLike): line is QuoteIntakeLineView {
  return "product" in line;
}

function viewLine(line: LineLike): QuoteIntakeLineView {
  if (isViewLine(line)) return line;
  return {
    index: line.index,
    state: line.state === "matched" && line.productId ? "matched" : "unmatched",
    product: line.productId
      ? { productId: line.productId, name: "", code: null, billing: null, listPrice: line.unitPrice ?? 0, quotable: true, blockers: [] }
      : null,
    why: line.why,
    codeMatch: line.codeMatch ? { code: line.codeMatch.code, outcome: line.codeMatch.outcome, products: [] } : null,
    suggestions: [],
    qty: line.qty,
    unitPrice: line.unitPrice,
    billingDiffers: line.billingDiffers,
  };
}

function blankEdit(c: IntakeContact): ContactEdit {
  return { firstName: c.firstName ?? "", lastName: c.lastName ?? "", email: c.email ?? "", phone: c.phone ?? "", title: c.title ?? "" };
}

export function startReview(intake: Reviewable): Review {
  const x = intake.extraction?.result;
  const c = intake.match?.customer;
  const a = x?.customer.address;
  const contacts = x?.contacts ?? [];
  const primary = contacts.findIndex((p) => p.role === "primary");
  return {
    customer: c?.kind === "existing" ? { kind: "existing", id: c.autotaskCompanyId, why: c.why } : { kind: "new" },
    form: {
      name: x?.customer.name ?? "",
      phone: x?.customer.phone ?? "",
      line1: a?.line1 ?? "",
      line2: a?.line2 ?? "",
      city: a?.city ?? "",
      state: a?.state ?? "",
      postalCode: a?.postalCode ?? "",
    },
    checked: contacts.map(() => true),
    edits: contacts.map(blankEdit),
    added: [],
    primary: contacts.length ? Math.max(0, primary) : -1,
    existingPrimaryId: null,
    term: x?.term.months ?? (x?.term.monthToMonth ? "month_to_month" : null),
    lines: (intake.match?.lines ?? []).map(() => ({ pick: null, out: false })),
    note: "",
  };
}

export type LineState =
  | { kind: "matched"; productId: string }
  | { kind: "switched_off"; productId: string }
  | { kind: "picked"; productId: string }
  | { kind: "fixing"; productId: string }
  | { kind: "out" }
  | { kind: "open" };

export function lineState(line: LineLike, choice: LineChoice | undefined, quotable?: (id: string) => boolean): LineState {
  const m = viewLine(line);
  if (choice?.out) return { kind: "out" };
  if (choice?.pick) {
    const ref = m.product?.productId === choice.pick ? m.product : m.suggestions.find((s) => s.productId === choice.pick);
    const on = ref ? ref.quotable : (quotable?.(choice.pick) ?? true);
    return on ? { kind: "picked", productId: choice.pick } : { kind: "fixing", productId: choice.pick };
  }
  if (m.state === "matched" && m.product) return { kind: "matched", productId: m.product.productId };
  if (m.state === "switched_off" && m.product) return { kind: "switched_off", productId: m.product.productId };
  return { kind: "open" };
}

// ------------------------------------------------------------------ product codes (#2057)
// What a line's code match says, worded as the mock draws it
// (docs/plans/lens-dealer-portal/product-codes.html). Staff see every product that has the
// code; a dealer sees only what it can quote.

/** Who is looking, and for staff the whole catalog: it counts the products sharing a code and names the OneBill code one bills on. */
export interface CodeWordsContext {
  staff: boolean;
  dealerName: string;
  records: readonly CatalogProductV2[] | null;
}

export type OneBillNotInOmni = CatalogSyncState["notInOmni"]["onebill"][number];

function codeKey(code: string | null | undefined): string {
  return onebillCodeStem(code).toUpperCase();
}

const NAME_WHY = { name: "By name", product_key: "By name, with the words in another order" } as const;

/** Why a matched or switched-off line matched. Null for a line with no code match: no printed code, or read before codes. */
export function matchedWhy(m: QuoteIntakeLineView, ctx: CodeWordsContext): string | null {
  const cm = m.codeMatch;
  if (!cm) return null;
  const c = cm.code;
  if (m.why === "name" || m.why === "product_key") return `${NAME_WHY[m.why]}. No ${ctx.staff ? "" : `${ctx.dealerName} `}product has code ${c}.`;
  if (m.why !== "code") return null;
  const ref = m.product;
  const own = Boolean(ref && codeKey(ref.code) === codeKey(c));
  if (!ctx.staff) return own ? `By code ${c}` : `By OneBill code ${c}`;
  const n = cm.products.length;
  if (n > 1) return own ? `By code ${c}. ${n} products have this code; this one has the line\u2019s name.` : `By OneBill code ${c}. ${n} products bill on it; this one has the line\u2019s name.`;
  if (!own) {
    const billsOn = ctx.records?.find((p) => p.id === ref?.productId)?.onebill?.productCode;
    if (!billsOn) return `By OneBill code ${c}.`;
    return `By OneBill code ${c}. It bills on ${billsOn}, so it\u2019s this product${ref?.code ? ` even though its own code is ${ref.code}` : ""}.`;
  }
  const shared = ctx.records?.filter((p) => codeKey(p.code) === codeKey(c)).length ?? 0;
  if (shared > 1 && ref?.quotable) return `By code ${c}. ${shared} products have this code; this is the one dealers can quote.`;
  return `By code ${c}`;
}

/** What a line Omni couldn't match says: its heading, why, whose catalog the suggestions come from and their label, and which share its code. */
export interface OpenWords {
  title: string;
  why: string;
  where: string;
  label: string;
  sameCode: ReadonlySet<string>;
}

export function openWords(m: QuoteIntakeLineView, printedCode: string | null, ctx: CodeWordsContext): OpenWords {
  const cm = m.codeMatch;
  // Staff match against every product, so "no product" is the whole catalog.
  const where = ctx.staff ? "Phoneware" : ctx.dealerName;
  if (ctx.staff && cm?.outcome === "ambiguous") {
    return {
      title: "Pick one",
      why: `More than one product has code ${cm.code}, and ${cm.products.length === 2 ? "neither" : "none"} has the line\u2019s name.`,
      where,
      label: "Same code, then closest by name",
      sameCode: new Set(cm.products.map((p) => p.productId)),
    };
  }
  const who = ctx.staff ? "" : `${ctx.dealerName} `;
  const why = cm ? `No ${who}product has code ${cm.code}, and none has this name.` : printedCode ? `No ${who}product has this code or this name.` : `No ${who}product has this name.`;
  return { title: `Not in ${where}\u2019s catalog`, why, where, label: `Closest in ${where}\u2019s catalog`, sameCode: new Set() };
}

/** The OneBill product the last catalog check found with this code and no Omni product billing on it, with its plan when it has one active plan. */
export function onebillWithCode(code: string, rows: readonly OneBillNotInOmni[]): { productCode: string; plan: OneBillNotInOmni["plans"][number] | null } | null {
  const key = codeKey(code);
  if (!key) return null;
  const row = rows.find((r) => codeKey(r.productCode) === key);
  if (!row) return null;
  const active = row.plans.filter((p) => p.active);
  return { productCode: row.productCode, plan: active.length === 1 ? active[0]! : null };
}

/** The code a product has that OneBill has no product for, from the last catalog check's code report. */
export function onebillLacksCode(productId: string, report: CatalogCodeReport | null | undefined): string | null {
  return report?.noOneBill.find((entry) => entry.products.some((p) => p.id === productId))?.code ?? null;
}

export interface DraftLine {
  index: number;
  productId: string;
  product: PricedProduct | null;
  qty: number | null;
  unitPrice: Cents | null;
}

function priceOf(m: QuoteIntakeLineView, product: PricedProduct | null): Cents | null {
  return m.unitPrice ?? product?.listPrice ?? null;
}

export function draftLines(intake: Reviewable, review: Review, catalog: PricedProduct[], quotable?: (id: string) => boolean): DraftLine[] {
  return (intake.match?.lines ?? []).flatMap((raw) => {
    const m = viewLine(raw);
    const s = lineState(m, review.lines[m.index], quotable);
    if (s.kind !== "matched" && s.kind !== "picked") return [];
    const product = catalog.find((p) => p.id === s.productId) ?? null;
    return [{ index: m.index, productId: s.productId, product, qty: m.qty, unitPrice: priceOf(m, product) }];
  });
}

export function belowFloor(lines: DraftLine[]): DraftLine[] {
  return lines.filter((l) => l.product?.floorPrice != null && l.unitPrice != null && l.unitPrice < l.product.floorPrice);
}

export function billingDiffers(raw: LineLike, line: IntakeLine, s: LineState, product: PricedProduct | null): boolean {
  const m = viewLine(raw);
  if (s.kind === "matched" || s.kind === "switched_off") return m.billingDiffers;
  if ((s.kind !== "picked" && s.kind !== "fixing") || !product) return false;
  const printed = printedBilling(line);
  return printed !== "unknown" && printed !== product.billing;
}

export function editOf(review: Review, index: number, extracted: IntakeContact): ContactEdit {
  return review.edits[index] ?? blankEdit(extracted);
}

export function alreadyContact(c: { email?: string | null }, review: Review, existing: CustomerContact[] | null): boolean {
  if (review.customer.kind !== "existing" || !existing || !c.email) return false;
  const email = c.email.trim().toLowerCase();
  return existing.some((x) => x.email?.trim().toLowerCase() === email);
}

export interface GateContext {
  catalog: PricedProduct[];
  existingContacts: CustomerContact[] | null;
  customerName: string;
  /** The intake's dealer, named when a switched-off line blocks Confirm. */
  dealerName?: string;
  quotable?: (id: string) => boolean;
}

export interface Gate {
  ok: boolean;
  msg: string;
}

function primaryDraft(review: Review, extracted: IntakeContact[]): ContactEdit | null {
  if (review.existingPrimaryId != null) return null;
  if (review.primary >= 0 && review.primary < extracted.length) return editOf(review, review.primary, extracted[review.primary]!);
  return null;
}

function addedNames(review: Review, extracted: IntakeContact[], existing: CustomerContact[] | null): string[] {
  const fromQuote = extracted.flatMap((c, i) => {
    const edit = editOf(review, i, c);
    if (!review.checked[i] || alreadyContact(edit, review, existing)) return [];
    return [contactName(edit)];
  });
  return [...fromQuote, ...review.added.filter((c) => c.include).map(contactName)];
}

export function gate(intake: Reviewable, review: Review, ctx: GateContext): Gate {
  const x = intake.extraction?.result;
  const matches = intake.match?.lines ?? [];
  const dealer = ctx.dealerName ?? "the dealer";
  const open = matches.filter((m) => lineState(m, review.lines[viewLine(m).index], ctx.quotable).kind === "open").length;
  if (open) return { ok: false, msg: `Pick a product or leave out ${open === 1 ? "the open line" : `the ${open} open lines`} first.` };
  const off = matches.flatMap((raw) => {
    const m = viewLine(raw);
    const kind = lineState(m, review.lines[m.index], ctx.quotable).kind;
    if (kind !== "switched_off" && kind !== "fixing") return [];
    return [x?.lines[m.index] ? lineText(x.lines[m.index]!) : `Line ${m.index + 1}`];
  });
  if (off.length === 1) return { ok: false, msg: `${off[0]} needs a product ${dealer} can quote. Switch it on, pick another or leave it out.` };
  if (off.length) return { ok: false, msg: `${off.length} lines need a product ${dealer} can quote: ${joinAnd(off)}. Switch them on, pick others or leave them out.` };
  const lines = draftLines(intake, review, ctx.catalog, ctx.quotable);
  if (!lines.length) return { ok: false, msg: "Every line is left out, so there is nothing to quote." };
  const noQty = lines.find((l) => l.qty == null);
  if (noQty && x) return { ok: false, msg: `${lineText(x.lines[noQty.index]!)} has no quantity on the quote. Leave it out, and add it in the draft after you confirm.` };
  const noPrice = lines.find((l) => l.unitPrice == null);
  if (noPrice && x) return { ok: false, msg: `${lineText(x.lines[noPrice.index]!)} has no price on the quote or in the catalog. Leave it out, and add it in the draft after you confirm.` };
  if (review.customer.kind === "new") {
    const f = review.form;
    if (!f.name.trim() || !f.phone.trim()) return { ok: false, msg: "Add the company name and main phone for the new prospect." };
    if (!f.line1.trim() || !f.city.trim() || !f.state.trim() || !f.postalCode.trim()) return { ok: false, msg: "Add the street, city, state and ZIP for the new prospect." };
  }
  if (review.term == null) return { ok: false, msg: "Pick the contract term. The quote doesn\u2019t say." };
  const contacts = x?.contacts ?? [];
  if (review.existingPrimaryId == null) {
    const pc = primaryDraft(review, contacts);
    const included = review.primary >= 0 && (review.checked[review.primary] || alreadyContact(pc ?? {}, review, ctx.existingContacts));
    if (!pc || !included) return { ok: false, msg: "Pick a primary contact. They sign the QuoteLink." };
    if (!pc.email.trim()) return { ok: false, msg: `${contactName(pc)} has no email, so they can\u2019t sign the QuoteLink. Add one, or pick another primary contact.` };
    if (review.customer.kind === "new" && !(pc.firstName.trim() && pc.lastName.trim())) {
      return { ok: false, msg: `Add the first and last name of ${contactName(pc)}. A new prospect\u2019s primary contact needs both, and an email.` };
    }
  }
  const low = belowFloor(lines);
  if (low.length && !review.note.trim()) return { ok: false, msg: "Say why the price is below the floor. Phoneware reads it when they approve." };
  const adds = addedNames(review, contacts, ctx.existingContacts);
  const bits: string[] = [];
  if (review.customer.kind === "new") bits.push(`adds ${ctx.customerName} to Autotask as a Prospect`);
  if (adds.length) bits.push(`adds ${joinAnd(adds)} as ${adds.length === 1 ? "a contact" : "contacts"}`);
  bits.push(low.length ? `creates a quote with ${plural(lines.length, "line")} for Phoneware to approve` : `creates a draft quote with ${plural(lines.length, "line")}`);
  return { ok: true, msg: `Ready. Confirm ${joinAnd(bits)}.` };
}

export function confirmRequest(intake: Reviewable, review: Review, ctx: GateContext): IntakeConfirmRequest {
  const extracted = intake.extraction?.result.contacts ?? [];
  const sent: IntakeConfirmContactInput[] = [];
  const existingPrimary = review.existingPrimaryId != null;
  extracted.forEach((c, i) => {
    const edit = editOf(review, i, c);
    const primary = !existingPrimary && i === review.primary;
    const add = review.checked[i] && !alreadyContact(edit, review, ctx.existingContacts);
    if (!primary && !add) return;
    sent.push({
      firstName: edit.firstName.trim(),
      lastName: edit.lastName.trim(),
      email: edit.email.trim() || null,
      phone: edit.phone.trim() || null,
      title: edit.title.trim() || null,
      role: primary ? "primary" : c.role === "primary" ? "other" : c.role,
    });
  });
  for (const added of review.added) {
    if (!added.include) continue;
    sent.push({
      firstName: added.firstName.trim(),
      lastName: added.lastName.trim(),
      email: added.email.trim() || null,
      phone: added.phone.trim() || null,
      title: added.title.trim() || null,
      role: "other",
    });
  }
  const lines: IntakeConfirmLineDecision[] = (intake.match?.lines ?? []).map((raw) => {
    const m = viewLine(raw);
    const s = lineState(m, review.lines[m.index], ctx.quotable);
    if (s.kind === "out" || s.kind === "open" || s.kind === "fixing" || s.kind === "switched_off") return { index: m.index, omit: true };
    const product = ctx.catalog.find((p) => p.id === s.productId) ?? null;
    return { index: m.index, productId: s.productId, qty: m.qty ?? undefined, unitPrice: priceOf(m, product) ?? undefined };
  });
  const f = review.form;
  const request: IntakeConfirmRequest = {
    customer:
      review.customer.kind === "existing"
        ? { kind: "existing", autotaskCompanyId: review.customer.id }
        : {
            kind: "new",
            name: f.name.trim(),
            phone: f.phone.trim(),
            address: { line1: f.line1.trim(), line2: f.line2.trim() || null, city: f.city.trim(), state: f.state.trim(), postalCode: f.postalCode.trim() },
          },
    contacts: sent,
    lines,
  };
  if (existingPrimary && review.existingPrimaryId != null) request.primaryContactId = review.existingPrimaryId;
  if (review.term != null) request.term = review.term;
  if (belowFloor(draftLines(intake, review, ctx.catalog, ctx.quotable)).length) request.approvalNote = review.note.trim();
  return request;
}

export interface TotalsCheck {
  rows: Array<{ label: string; printed: Cents | null; draft: Cents }>;
  why: Array<{ icon: "circle-alert" | "minus" | "info"; text: string }>;
}

export function totalsCheck(intake: Reviewable, review: Review, catalog: PricedProduct[], quotable?: (id: string) => boolean): TotalsCheck {
  const lines = draftLines(intake, review, catalog, quotable);
  const sum = (billing: "monthly" | "one_time") => lines.filter((l) => l.product?.billing === billing).reduce((s, l) => s + (l.qty ?? 0) * (l.unitPrice ?? 0), 0);
  const totals = intake.match?.totals;
  const why: TotalsCheck["why"] = [];
  const printed = intake.extraction?.result.lines ?? [];
  for (const raw of intake.match?.lines ?? []) {
    const m = viewLine(raw);
    const l = printed[m.index];
    if (!l) continue;
    const s = lineState(m, review.lines[m.index], quotable);
    const amount = [l.extended, billWord(printedBilling(l))].filter(Boolean).join(" ");
    if (s.kind === "open") why.push({ icon: "circle-alert", text: `Not counted until you pick a product or leave it out: ${lineText(l)}, ${amount}.` });
    if (s.kind === "switched_off" || s.kind === "fixing") why.push({ icon: "circle-alert", text: `Not counted until its product is switched on, or you pick another or leave it out: ${lineText(l)}, ${amount}.` });
    if (s.kind === "out") why.push({ icon: "minus", text: `Left out: ${lineText(l)}${amount ? `, ${amount}` : ""}.` });
    if (s.kind === "matched" || s.kind === "picked") {
      const product = catalog.find((p) => p.id === s.productId) ?? null;
      if (product && billingDiffers(m, l, s, product)) {
        why.push({ icon: "info", text: `${lineText(l)} is ${billWord(printedBilling(l))} on the quote and ${billWord(product.billing)} in Omni${l.extended ? `, ${l.extended}` : ""}.` });
      }
    }
  }
  return {
    rows: [
      { label: "Monthly", printed: totals?.monthlyPrinted ?? null, draft: sum("monthly") },
      { label: "One-time", printed: totals?.oneTimePrinted ?? null, draft: sum("one_time") },
    ],
    why,
  };
}

export interface Confirmation {
  intakeId: string;
  intakeNumber: string;
  filename: string;
  dealerName: string;
  newCustomer: boolean;
  customerName: string;
  accountNumber: string | null;
  added: string[];
  needsApproval: boolean;
  belowFloor: number;
  omitted: string[];
  signer: string | null;
}

const confirmations = new Map<string, Confirmation>();

export function rememberConfirmation(quoteId: string, c: Confirmation): void {
  confirmations.set(quoteId, c);
}

export function confirmationFor(quoteId: string): Confirmation | null {
  return confirmations.get(quoteId) ?? null;
}
